/**
 * Common state management capabilities (on/off/toggle)
 * Used by relays, dimmers, and lights
 */

/**
 * Standard turn action for switching devices on/off
 * @param {string} method - RPC method ('Switch.Set' or 'Light.Set')
 * @param {string} stateProperty - Property name for state ('on' for lights, 'on' for switches)
 */
export function createTurnAction(method = 'Switch.Set', stateProperty = 'on') {
	return {
		name: 'Turn On/Off',
		description: 'Turn the device on, off, or toggle its current state',
		parameters: {
			state: {
				type: 'enum',
				required: true,
				values: ['on', 'off', 'toggle'],
				default: 'on',
				description: 'Desired state',
			},
			timer: {
				type: 'number',
				required: false,
				min: 1,
				max: 7200,
				unit: 'seconds',
				description: 'Auto-off timer duration',
			},
		},
		executeAction: async (device, channel, params) => {
			const { state, timer } = params;
			const channelId = parseInt(channel.split(':')[1]) || 0;

			const payload = {
				id: channelId,
			};

			if (state === 'toggle') {
				const currentState = device.status?.[channel]?.output;
				payload[stateProperty] = !currentState;
			} else {
				payload[stateProperty] = state === 'on';
			}

			if (timer) {
				payload.toggle_after = timer;
			}

			return {
				method,
				params: payload,
			};
		},
	};
}

/**
 * Standard state condition (is device on/off)
 */
export function createStateCondition(stateProperty = 'output') {
	return {
		name: 'Device State',
		description: 'Check if the device is currently on or off',
		parameters: {
			state: {
				type: 'boolean',
				required: true,
				description: 'Expected state (true = on, false = off)',
			},
		},
		evaluateCondition: (device, channel, params) => {
			const currentState = device.status?.[channel]?.[stateProperty];
			return currentState === params.state;
		},
	};
}

/**
 * Standard state change trigger
 */
export function createStateChangedTrigger(stateProperty = 'output') {
	return {
		name: 'State Changed',
		description: 'Fires when the device state changes',
		parameters: {
			fromState: {
				type: 'enum',
				required: false,
				values: ['on', 'off', 'any'],
				default: 'any',
				description: 'Previous state filter',
			},
			toState: {
				type: 'enum',
				required: false,
				values: ['on', 'off', 'any'],
				default: 'any',
				description: 'New state filter',
			},
		},
		detectTrigger: (previousState, currentState, channel, params) => {
			const prevOutput = previousState?.status?.[channel]?.[stateProperty];
			const currOutput = currentState?.status?.[channel]?.[stateProperty];

			if (prevOutput === currOutput) return false;

			const { fromState = 'any', toState = 'any' } = params;

			const fromMatch =
				fromState === 'any' ||
				(fromState === 'on' && prevOutput === true) ||
				(fromState === 'off' && prevOutput === false);

			const toMatch =
				toState === 'any' ||
				(toState === 'on' && currOutput === true) ||
				(toState === 'off' && currOutput === false);

			return fromMatch && toMatch;
		},
	};
}
